import _styled, { default as _styled2, default as _styled3 } from "styled-components";
import React from "react";
import NextHead from "next/head";
import { hasItems } from "~/features/app/utils";
type Props = {
  height: number; // in full viewport height units
  onProgress: (progress: number) => void;
  frames: string[];
};
export function ScrollSequence(props: Props) {
  const {
    height,
    onProgress,
    frames
  } = props;
  const [hasScrolled, setHasScrolled] = React.useState(false);
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const isDrawing = React.useRef(false); // to handle race condition

  React.useEffect(() => {
    const canvas = canvasRef.current;
    const img = new Image();
    if (!canvas) {
      return;
    }
    const context = canvas.getContext("2d");
    if (!context) {
      return;
    }
    function drawImage(index: number) {
      if (isDrawing.current || !canvas || !context) {
        return;
      }
      img.onload = () => {
        canvas.width = img.width;
        canvas.height = img.height;
        context.drawImage(img, 0, 0);
        isDrawing.current = false;
      };
      img.onerror = () => {
        isDrawing.current = false;
      };
      isDrawing.current = true;
      img.src = frames[index] || "";
    }
    function onScroll() {
      setHasScrolled(true);
      const scrollTop = document.documentElement.scrollTop;
      const maxScrollTop = window.innerHeight * (height - 1);
      const scrollFraction = scrollTop / maxScrollTop;
      const frameIndex = Math.min(frames.length - 1, Math.ceil(scrollFraction * frames.length));
      requestAnimationFrame(() => drawImage(frameIndex + 1));
      onProgress?.(scrollFraction);
    }
    window.addEventListener("scroll", onScroll);
    drawImage(0);
    return () => {
      window.removeEventListener("scroll", onScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (!hasItems(frames)) {
    return null;
  }
  return <>
      <NextHead>
        {frames.map(frame => <link key={`preload-frame-${frame}`} rel="preload" as="image" href={frame} />)}
      </NextHead>
      <_StyledDiv>
        {!hasScrolled && frames[0] && <_StyledImg src={frames[0]} alt="" loading="eager" />}
        <_StyledCanvas ref={canvasRef} onContextMenu={e => e.preventDefault()} />
      </_StyledDiv>
    </>;
}
var _StyledDiv = _styled("div").withConfig({
  componentId: "sc-sf-189iot5-0"
})({
  "position": "sticky",
  "inset": "0",
  "width": "100%"
});
var _StyledImg = _styled2("img").withConfig({
  componentId: "sc-sf-189iot5-1"
})({
  "pointerEvents": "none",
  "position": "absolute",
  "inset": "0",
  "height": "100%",
  "width": "100%",
  "objectFit": "cover"
});
var _StyledCanvas = /*#__PURE__*/_styled3("canvas").withConfig({
  displayName: "scroll-sequence___StyledCanvas",
  componentId: "sc-vepehy-0"
})({
  "pointerEvents": "none",
  "height": "100svh",
  "width": "100%",
  "--tw-bg-opacity": "1",
  "backgroundColor": "rgb(0 7 4 / var(--tw-bg-opacity))",
  "objectFit": "cover"
});